
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

interface BreadcrumbRoute {
  path: string;
  label: string;
}

interface BreadcrumbProps {
  customRoutes?: BreadcrumbRoute[];
}

const Breadcrumb = ({ customRoutes }: BreadcrumbProps) => {
  const location = useLocation();
  
  // Define route mapping for prettier labels
  const routeLabels: Record<string, string> = {
    '': 'Home',
    'over-ons': 'Over Ons',
    'diensten': 'Diensten',
    'waarom': 'Waarom Wij',
    'contact': 'Contact',
    'blog': 'Blog',
    'calculator': 'Calculator',
  };

  // Generate breadcrumb paths from current location or use custom routes
  const routes = customRoutes || location.pathname
    .split('/')
    .filter(Boolean)
    .map((path, index, array) => {
      const url = '/' + array.slice(0, index + 1).join('/');
      return {
        path: url,
        label: routeLabels[path] || path.replace(/-/g, ' '),
      };
    });

  return (
    <nav aria-label="Breadcrumb" className="container py-3 text-sm">
      <ol className="flex flex-wrap items-center" itemScope itemType="https://schema.org/BreadcrumbList">
        <li 
          className="flex items-center" 
          itemProp="itemListElement" 
          itemScope 
          itemType="https://schema.org/ListItem"
        >
          <Link 
            to="/" 
            className="text-gray-600 hover:text-brand-blue transition-colors"
            itemProp="item"
          >
            <span itemProp="name">Home</span>
          </Link>
          <meta itemProp="position" content="1" />
          {routes.length > 0 && (
            <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
          )}
        </li>

        {routes.map((route, index) => (
          <li 
            key={route.path}
            className="flex items-center"
            itemProp="itemListElement" 
            itemScope 
            itemType="https://schema.org/ListItem"
          >
            {index < routes.length - 1 ? (
              <>
                <Link 
                  to={route.path} 
                  className="text-gray-600 hover:text-brand-blue transition-colors"
                  itemProp="item"
                >
                  <span itemProp="name">{route.label}</span>
                </Link>
                <meta itemProp="position" content={`${index + 2}`} />
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
              </>
            ) : (
              <>
                <span 
                  className="text-brand-blue font-medium"
                  itemProp="name"
                >
                  {route.label}
                </span>
                <meta itemProp="position" content={`${index + 2}`} />
              </>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
